import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Trash2, Mail, Clock, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";
import { sanitizeError } from "@/lib/errorUtils";

const inviteSchema = z.object({ email: z.string().email(), role: z.enum(["viewer", "admin"]) });

interface Member { id: string; user_id: string; role: string; profile?: { name: string; id: string }; }
interface Invitation { id: string; email: string; role: string; expires_at: string; accepted_at: string | null; }

const TeamMembers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState({ email: "", role: "viewer" as "viewer" | "admin" });

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);

      const [membersRes, invitationsRes, companyRes] = await Promise.all([
        supabase.from("company_members").select("*").eq("company_id", id).order("created_at"),
        supabase.from("invitations").select("*").eq("company_id", id).is("accepted_at", null).order("created_at", { ascending: false }),
        supabase.from("companies").select("name").eq("id", id).single(),
      ]);

      if (membersRes.error) throw membersRes.error;
      setCompanyName(companyRes.data?.name || "");
      
      const membersWithProfiles = await Promise.all(
        (membersRes.data || []).map(async (m) => {
          const { data: profile } = await supabase.from("profiles").select("id, name").eq("id", m.user_id).single();
          return { ...m, profile: profile || undefined };
        })
      );

      setMembers(membersWithProfiles);
      setInvitations(invitationsRes.data || []);
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load team"));
    } finally { setLoading(false); }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const validated = inviteSchema.parse(form);
      const existingInvite = invitations.find(i => i.email.toLowerCase() === validated.email.toLowerCase());
      if (existingInvite) throw new Error("Invitation already pending");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const token = Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => b.toString(16).padStart(2, '0')).join('');
      const expiresAt = new Date(); expiresAt.setDate(expiresAt.getDate() + 7);

      const { error } = await supabase.from("invitations").insert({
        company_id: id!, email: validated.email, role: validated.role,
        invited_by: user.id, token, expires_at: expiresAt.toISOString(),
      });
      if (error) throw error;

      toast.success(`Invitation sent to ${validated.email}`);
      setDialogOpen(false);
      setForm({ email: "", role: "viewer" });
      loadData();
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to invite");
    } finally { setSubmitting(false); }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    if (!confirm("Cancel this invitation?")) return;
    try {
      const { error } = await supabase.from("invitations").delete().eq("id", invitationId);
      if (error) throw error;
      toast.success("Invitation cancelled");
      loadData();
    } catch (error: unknown) { toast.error(sanitizeError(error, "Failed to cancel")); }
  };

  const handleRemoveMember = async (memberId: string, memberUserId: string) => {
    if (memberUserId === currentUserId) { toast.error("You cannot remove yourself"); return; }
    if (!confirm("Remove this member?")) return;
    try {
      const { error } = await supabase.from("company_members").delete().eq("id", memberId);
      if (error) throw error;
      toast.success("Member removed");
      loadData();
    } catch (error: unknown) { toast.error(sanitizeError(error, "Failed to remove")); }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) { case "owner": return "bg-primary/10 text-primary"; case "admin": return "bg-warning/10 text-warning"; default: return "bg-muted text-muted-foreground"; }
  };

  return (
    <AppShell title="Team" subtitle={companyName} showBack backTo={`/app/companies/${id}`} companyId={id} companyName={companyName}>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">Manage who has access</p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild><Button className="w-full sm:w-auto rounded-xl"><UserPlus className="h-4 w-4 mr-2" />Invite member</Button></DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Invite team member</DialogTitle><DialogDescription>Send an email invitation</DialogDescription></DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="colleague@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v: "viewer" | "admin") => setForm({ ...form, role: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="viewer">Viewer</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-xl">Cancel</Button>
                    <Button type="submit" disabled={submitting} className="flex-1 rounded-xl">{submitting ? "Sending..." : "Send"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <>
              <div className="space-y-3">
                <h2 className="text-lg font-semibold flex items-center gap-2"><Users className="h-5 w-5" />Members ({members.length})</h2>
                {members.map((m) => (
                  <Card key={m.id} className="rounded-2xl">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-semibold">{m.profile?.name?.charAt(0)?.toUpperCase() || "?"}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{m.profile?.name || "Unknown"}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(m.role)}`}>{m.role}</span>
                          </div>
                        </div>
                        {m.role !== "owner" && m.user_id !== currentUserId && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(m.id, m.user_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {invitations.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2"><Clock className="h-5 w-5" />Pending ({invitations.length})</h2>
                  {invitations.map((inv) => (
                    <Card key={inv.id} className="rounded-2xl border-dashed">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Mail className="h-5 w-5 text-muted-foreground" /></div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{inv.email}</p>
                              <p className="text-xs text-muted-foreground">Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(inv.role)}`}>{inv.role}</span>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteInvitation(inv.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default TeamMembers;
